import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Spinner } from "reactstrap";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { getBookmarks, getPlaylistBookmarks, toggleBookmark, togglePlaylistBookmark } from "./service/auth";
import { getVideoUrl, getPlaylistUrl } from "./utils/urlHelpers";
import "./Library.css";

const Library = () => {
  const [bookmarks, setBookmarks] = useState([]);
  const [playlistBookmarks, setPlaylistBookmarks] = useState([]);
  const [activeTab, setActiveTab] = useState("videos");
  const [loadingByTab, setLoadingByTab] = useState({
    videos: true,
    playlists: false,
  });
  const [errorByTab, setErrorByTab] = useState({
    videos: null,
    playlists: null,
  });



  const setTabLoading = useCallback((tab, isLoading) => {
    setLoadingByTab((prev) => ({
      ...prev,
      [tab]: isLoading,
    }));
  }, []);

  const setTabError = useCallback((tab, error) => {
    setErrorByTab((prev) => ({
      ...prev,
      [tab]: error,
    }));
  }, []);

  const fetchVideoBookmarks = useCallback(async () => {
    try {
      setTabLoading("videos", true);
      const response = await getBookmarks();
      setBookmarks(response.data?.data?.bookmarks || []);
      setTabError("videos", null);
    } catch (err) {
      console.error("Failed to fetch video bookmarks:", err);
      setTabError("videos", "Unable to load your saved videos. Please try again.");
    } finally {
      setTabLoading("videos", false);
    }
  }, [setTabError, setTabLoading]);

  const fetchPlaylistBookmarks = useCallback(async () => {
    try {
      setTabLoading("playlists", true);
      const response = await getPlaylistBookmarks();
      setPlaylistBookmarks(response.data?.data?.bookmarks || response.data?.data?.playlists || []);
      setTabError("playlists", null);
    } catch (err) {
      console.error("Failed to fetch playlist bookmarks:", err);
      setTabError("playlists", "Unable to load your saved playlists. Please try again.");
    } finally {
      setTabLoading("playlists", false);
    }
  }, [setTabError, setTabLoading]);

  const fetchActiveTabBookmarks = useCallback((tab = activeTab) => {
    if (tab === "videos") {
      return fetchVideoBookmarks();
    }

    return fetchPlaylistBookmarks();
  }, [activeTab, fetchPlaylistBookmarks, fetchVideoBookmarks]);

  useEffect(() => {
    fetchActiveTabBookmarks(activeTab);
  }, [activeTab, fetchActiveTabBookmarks]);

  const handleRemoveBookmark = async (videoId, e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await toggleBookmark(videoId);
      setBookmarks(bookmarks.filter(video => video.id !== videoId));
    } catch (err) {
      console.error("Failed to remove bookmark:", err);
    }
  };

  const handleRemovePlaylistBookmark = async (playlistId, e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await togglePlaylistBookmark(playlistId);
      setPlaylistBookmarks(playlistBookmarks.filter(playlist => playlist.id !== playlistId));
    } catch (err) {
      console.error("Failed to remove playlist bookmark:", err);
    }
  };

  const activeItems = activeTab === "videos" ? bookmarks : playlistBookmarks;
  const isVideosTab = activeTab === "videos";
  const isLoading = loadingByTab[activeTab];
  const error = errorByTab[activeTab];

  return (
    <div className="library-page">
      <Header />

      <div className="library-container">
        <h1 className="library-title">My Library</h1>
        <p className="library-subtitle">Your saved music videos and playlists</p>

        <div className="library-tabs" role="tablist" aria-label="Library bookmark types">
          <button
            type="button"
            className={`library-tab ${isVideosTab ? "active" : ""}`}
            onClick={() => setActiveTab("videos")}
            role="tab"
            aria-selected={isVideosTab}
          >
            Videos
          </button>
          <button
            type="button"
            className={`library-tab ${!isVideosTab ? "active" : ""}`}
            onClick={() => setActiveTab("playlists")}
            role="tab"
            aria-selected={!isVideosTab}
          >
            Playlists
          </button>
        </div>

        {isLoading ? (
          <div className="library-loading">
            <Spinner color="warning" />
            <p>Loading your library...</p>
          </div>
        ) : error ? (
          <div className="library-error">
            <p>{error}</p>
            <button onClick={() => fetchActiveTabBookmarks()} className="library-retry-btn">
              Try Again
            </button>
          </div>
        ) : activeItems.length === 0 ? (
          <div className="library-empty">
            <div className="library-empty-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </div>
            <h2>No saved {isVideosTab ? "videos" : "playlists"} yet</h2>
            <p>Tap the star on any {isVideosTab ? "video" : "playlist"} to save it to your library</p>
            <Link to={isVideosTab ? "/discover" : "/allplaylists"} className="library-discover-btn">
              {isVideosTab ? "Discover Videos" : "View Playlists"}
            </Link>
          </div>
        ) : (
          <div className="library-grid">
            {isVideosTab ? (
              activeItems.map((video) => (
                <Link
                  key={video.id}
                  to={getVideoUrl(video)}
                  className="library-video-card"
                >
                  <div className="library-video-thumbnail">
                    <img src={video.thumbnail_url} alt={video.title} />
                    <div className="library-video-overlay">
                      <button
                        className="library-remove-btn"
                        onClick={(e) => handleRemoveBookmark(video.id, e)}
                        title="Remove from library"
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="library-video-info">
                    <h3 className="library-video-title">{video.title}</h3>
                    <p className="library-video-artist">{video.artist}</p>
                    {video.total_likes > 0 && (
                      <span className="library-video-likes">
                        {video.total_likes} {video.total_likes === 1 ? 'like' : 'likes'}
                      </span>
                    )}
                  </div>
                </Link>
              ))
            ) : (
              activeItems.map((playlist) => (
                <Link
                  key={playlist.id}
                  to={getPlaylistUrl(playlist)}
                  className="library-video-card"
                >
                  <div className="library-video-thumbnail">
                    <img src={playlist.image_url || playlist.thumbnail_url} alt={playlist.name || "Playlist"} />
                    <div className="library-video-overlay">
                      <button
                        className="library-remove-btn"
                        onClick={(e) => handleRemovePlaylistBookmark(playlist.id, e)}
                        title="Remove from library"
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="library-video-info">
                    <h3 className="library-video-title">{playlist.name}</h3>
                    <p className="library-video-artist">{playlist.description || "Playlist"}</p>
                  </div>
                </Link>
              ))
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Library;
